import firebase from "firebase/app";
import "firebase/functions";

import { appConfig } from "../../../utils";
import {
    buildStaffTrainingRecord,
    courseResult,
} from "../../../../functions/functions-common/agents/agents/generateTrainingCourse/schema.mjs";

export function databaseURL() {
    return appConfig?.firebaseConfig?.databaseURL || "";
}

export function courseRecord({ schema, prompt, procedureCheck, userId, status = "draft" }) {
    const record = {
        SchemaVersion: schema.schemaVersion,
        Status: status,
        Title: schema.meta.title,
        Description: schema.meta.description || "",
        EstimatedMinutes: schema.meta.estimatedMinutes,
        Difficulty: schema.meta.difficulty,
        Audience: schema.meta.audience || "",
        Schema: schema,
        SourcePrompt: prompt || "",
        CreatedBy: userId,
        Date: { Created: new Date().toISOString() },
        ProcedureCheck: procedureCheck,
    };
    if (schema.meta.materialId) record.MaterialID = schema.meta.materialId;
    return record;
}

export function conflictsOf(procedureCheck) {
    const conflicts = procedureCheck?.Conflicts;
    if (!conflicts) return [];
    return Array.isArray(conflicts) ? conflicts : Object.values(conflicts);
}

export function canSaveCourse(schemaResult, procedureCheck) {
    return Boolean(schemaResult?.ok) && conflictsOf(procedureCheck).length === 0 && Boolean(procedureCheck?.CheckedAt);
}

export async function generateCourse(payload) {
    const generate = firebase.app().functions().httpsCallable("generateTrainingCourse", { timeout: 300000 });
    const result = await generate({
        ...payload,
        databaseURL: databaseURL(),
    });
    return result.data;
}

export async function saveNewCourse(db, input) {
    const ref = await db.push("Home/Courses", courseRecord(input));
    return ref.key;
}

export async function updateCourse(db, id, input) {
    const record = courseRecord({ ...input, status: "draft" });
    delete record.Date;
    delete record.CreatedBy;
    await db.update(`Home/Courses/${id}`, record);
}

export async function setCourseStatus(db, id, status) {
    await db.update(`Home/Courses/${id}`, { Status: status });
}

export async function recordCourseCompletion(db, { course, schema, progress, userId }) {
    const result = courseResult(schema, progress);
    if (!result.passed) return { ...result, recordId: "" };
    if (progress?.RecordID) return { ...result, recordId: progress.RecordID };
    const record = buildStaffTrainingRecord({
        course: { ...schema, id: course.ID },
        score: result.score,
        passed: true,
        startedAt: progress?.Started,
        userId,
    });
    const ref = await db.push(`Userdata/${userId}/Training/Records`, record);
    await db.update(`Home/Courses/${course.ID}/Completions/${userId}`, {
        Date: record.Date.Complete,
        Score: result.score,
        Passed: true,
        RecordID: ref.key,
    });
    const materialId = course.MaterialID || schema.meta?.materialId;
    if (materialId) {
        await db.update(`Home/TrainingMaterial/${materialId}/Users/${userId}`, {
            Date: record.Date.Complete,
        });
    }
    return { ...result, recordId: ref.key, completedAt: record.Date.Complete };
}

export async function readFileAsBase64(file) {
    const contentBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const value = String(reader.result || "");
            resolve(value.includes(",") ? value.slice(value.indexOf(",") + 1) : value);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
    return { name: file.name, contentBase64 };
}
